/**
 * REST API Adapter for Self-Order App
 * Replaces Firebase Firestore calls with Node.js server REST API calls.
 * Firebase Auth is preserved — ID tokens are sent as Bearer tokens.
 *
 * Real-time order updates strategy:
 *   - The server mirrors each order to Firestore at:
 *       private_restaurants/{restaurantId}/orders_collection/{orderId}
 *   - The client listens via Firestore onSnapshot (same collection as before).
 *   - When a snapshot fires (order status changed), the client calls
 *       getSelfOrderById() to get the full authoritative data from MongoDB.
 *   - This gives real-time push without polling or WebSockets.
 */

import { firebaseAuth } from './firebaseAdapter.jsx';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.swadho.com/api/v1';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Get the current Firebase ID token for authenticated requests.
 * Returns null if user is not signed in and auto-auth fails.
 */
const getIdToken = async () => {
  let user = firebaseAuth.getCurrentUser();
  if (!user && firebaseAuth.getCurrentUserOrWait) {
    user = await firebaseAuth.getCurrentUserOrWait();
  }
  if (!user && firebaseAuth.signInAnonymously) {
    try {
      const res = await firebaseAuth.signInAnonymously();
      user = res.user;
    } catch (e) {
      console.warn("Auto guest auth on token refresh failed:", e);
    }
  }
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
};

/**
 * Authenticated fetch wrapper — attaches Bearer token automatically.
 */
const authFetch = async (url, options = {}) => {
  const token = await getIdToken();
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
};

// ─── Public Endpoints ─────────────────────────────────────────────────────────

/**
 * Fetch public restaurant details by ID.
 * Matches firebaseAdapter.getPublicRestaurant signature.
 */
export const getPublicRestaurant = async (restaurantId) => {
  try {
    const res = await fetch(`${BASE_URL}/self-order/restaurant/${restaurantId}`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch restaurant', code: json.code || 'FETCH_ERROR' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getPublicRestaurant error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Fetch menu items for a restaurant (Promise-based).
 * Used by RestaurantContext to include in a single Promise.all for parallel loading.
 */
export const getMenuItemsAsync = async (restaurantId) => {
  try {
    const res = await fetch(`${BASE_URL}/self-order/restaurant/${restaurantId}/menu`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch menu items' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getMenuItemsAsync error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch public table / seat details for QR resolution.
 */
export const getPublicTableDetails = async (restaurantId, tableId) => {
  if (!restaurantId || !tableId) return { success: false, error: 'Missing parameters' };
  try {
    const res = await fetch(`${BASE_URL}/self-order/restaurant/${restaurantId}/table/${tableId}`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch table details' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getPublicTableDetails error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch menu items for a restaurant (callback-based, legacy).
 * Matches firebaseAdapter.getMenuItems signature — returns an unsubscribe fn.
 * Since REST is not real-time, we fetch once and return a no-op unsubscribe.
 * Real-time menu updates are not critical for self-order (menu rarely changes mid-session).
 */
export const getMenuItems = (restaurantId, callback) => {
  let cancelled = false;

  const fetchItems = async () => {
    try {
      const res = await fetch(`${BASE_URL}/self-order/restaurant/${restaurantId}/menu`);
      const json = await res.json();
      if (cancelled) return;
      if (!res.ok || !json.success) {
        callback({ success: false, error: json.error || 'Failed to fetch menu items' });
        return;
      }
      callback({ success: true, data: json.data });
    } catch (error) {
      if (!cancelled) {
        console.error('getMenuItems error:', error);
        callback({ success: false, error: error.message });
      }
    }
  };

  fetchItems();

  // Return unsubscribe function (matches Firestore onSnapshot signature)
  return () => { cancelled = true; };
};

/**
 * Fetch restaurant theme/brand config.
 * Matches firebaseAdapter.getRestaurantTheme signature.
 */
export const getRestaurantTheme = async (restaurantId) => {
  try {
    const res = await fetch(`${BASE_URL}/self-order/restaurant/${restaurantId}/theme`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch theme', code: json.code || 'FETCH_ERROR' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getRestaurantTheme error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Stub — reviews not yet in MongoDB. Returns empty array gracefully.
 */
export const getCustomerReviews = async (_restaurantId) => {
  return { success: true, data: [] };
};

/**
 * Fetch self-order kiosk configuration for a restaurant.
 * Determines which mode (order / payAndOrder / showCode) and sign-in policy the kiosk uses.
 */
export const getSelfOrderSettings = async (restaurantId) => {
  try {
    const res = await fetch(`${BASE_URL}/self-order/restaurant/${restaurantId}/kiosk-config`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: true, data: { enabledModes: ['order'], requireSignIn: false, allowPayAndOrderWithoutSignIn: true } };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getSelfOrderSettings error:', error);
    return { success: true, data: { enabledModes: ['order'], requireSignIn: false, allowPayAndOrderWithoutSignIn: true } };
  }
};


// ─── Authenticated Endpoints ──────────────────────────────────────────────────

/**
 * Submit a self-order to the Node server.
 * Matches firebaseAdapter.submitOrder signature.
 */
export const submitOrder = async (orderData) => {
  try {
    const restaurantId = orderData.restaurant_id || orderData.restaurantId;
    if (!restaurantId) {
      return { success: false, error: 'restaurantId is required', code: 'VALIDATION_ERROR' };
    }

    // Normalize field names to camelCase for the server
    const rawOrderType = orderData.order_type || orderData.orderType || 'dine_in';
    const normalizedOrderType = rawOrderType === 'pickup' ? 'takeaway' : rawOrderType;

    const payload = {
      items: (orderData.items || []).map((item) => ({
        menuItemId: item.menu_item_id || item.menuItemId || item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        customizations: item.customizations || null,
        cookingInstruction: item.specialInstructions || item.cookingInstruction || null,
        category: item.category || null,
      })),
      orderType: normalizedOrderType,
      tableNumber: orderData.table_number || orderData.tableNumber,
      tableId: orderData.table_id || orderData.tableId,
      tableName: orderData.tableName || orderData.table_name,
      tableLocation: orderData.tableLocation || orderData.table_location,
      isTrackedTable: orderData.isTrackedTable !== undefined ? orderData.isTrackedTable : true,
      phone: orderData.phone,
      itemTotal: orderData.item_total || orderData.itemTotal,
      deliveryFee: orderData.delivery_fee || orderData.deliveryFee || 0,
      tipAmount: orderData.tip_amount || orderData.tipAmount || 0,
      discountAmount: orderData.discount_amount || orderData.discountAmount || 0,
      taxesAndCharges: orderData.taxes_and_charges || orderData.taxesAndCharges || 0,
      finalAmount: orderData.final_amount || orderData.finalAmount,
      paymentMethod: orderData.payment_method || orderData.paymentMethod || 'cod',
      promotionId: orderData.promotionId || orderData.promotion_id || null,
      promoCode: orderData.promoCode || orderData.promo_code,
      deliveryAddress: orderData.deliveryAddress || orderData.delivery_address,
    };

    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/orders`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    const json = await res.json();

    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to place order', code: json.code || 'ORDER_ERROR' };
    }
    return { success: true, data: json.data, message: json.message };
  } catch (error) {
    console.error('submitOrder error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Fetch a single order by ID (user can only access their own).
 * Used by OrderContext after a Firestore onSnapshot fires.
 */
export const getSelfOrderById = async (restaurantId, orderId) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/orders/${orderId}`
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch order', code: json.code || 'FETCH_ERROR' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getSelfOrderById error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Get all orders for the current user in a restaurant.
 * Matches firebaseAdapter.getOrdersByUserId signature — returns unsubscribe fn.
 *
 * Real-time strategy:
 *   - Initial fetch is done here.
 *   - Subsequent updates come via Firestore onSnapshot in OrderContext,
 *     which calls getSelfOrderById() for the changed order.
 */
export const getOrdersByUserId = (_userId, restaurantId, callback, params = {}) => {
  let cancelled = false;

  const fetchOrders = async () => {
    try {
      const { 
        limit = 20, 
        page = 1, 
        search = '', 
        timeRange = '',
        date = '',
        startDate = '',
        endDate = ''
      } = params;
      
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        page: page.toString(),
      });
      
      if (search) queryParams.append('search', search);
      if (timeRange) queryParams.append('timeRange', timeRange);
      if (date) queryParams.append('date', date);
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);

      const res = await authFetch(
        `${BASE_URL}/self-order/restaurant/${restaurantId}/orders?${queryParams.toString()}`
      );
      const json = await res.json();
      if (cancelled) return;
      if (!res.ok || !json.success) {
        callback({ success: false, error: json.error || 'Failed to fetch orders' });
        return;
      }
      // Normalize dates (server returns ISO strings, not Firestore Timestamps)
      const orders = (json.data || []).map((order) => ({
        ...order,
        created_at: order.createdAt ? new Date(order.createdAt) : new Date(),
      }));
      callback({ 
        success: true, 
        data: orders, 
        pagination: json.pagination 
      });
    } catch (error) {
      if (!cancelled) {
        console.error('getOrdersByUserId error:', error);
        callback({ success: false, error: error.message });
      }
    }
  };

  fetchOrders();

  return () => { cancelled = true; };
};

/**
 * Update order status (cancel only from self-order client).
 * Matches firebaseAdapter.updateOrderStatus signature.
 */
export const updateOrderStatus = async (orderId, newStatus, restaurantId) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/orders/${orderId}/status`,
      { method: 'PATCH', body: JSON.stringify({ status: newStatus }) }
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to update order status', code: json.code || 'UPDATE_ERROR' };
    }
    return { success: true, message: json.message };
  } catch (error) {
    console.error('updateOrderStatus error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Fetch active, currently-valid promotions for a restaurant.
 * Public endpoint — no auth required.
 */
export const getPromotions = async (restaurantId) => {
  try {
    const res = await fetch(`${BASE_URL}/self-order/restaurant/${restaurantId}/promotions`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch promotions', data: [] };
    }
    return { success: true, data: json.data || [] };
  } catch (error) {
    console.error('getPromotions error:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Create a cart-hold for showCode mode.
 * Customer's cart is saved server-side and a short alphanumeric pickup code is returned.
 * The cashier enters this code in the POS to load the cart.
 *
 * @route POST /api/v1/self-order/restaurant/:restaurantId/cart-hold
 * @access Firebase Auth
 */
export const createCartHold = async (restaurantId, { items, tableNumber = null, tableId = null, tableName = null, tableLocation = null, isTrackedTable = true, customerNote = '' }) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/cart-hold`,
      {
        method: 'POST',
        body: JSON.stringify({ items, tableNumber, tableId, tableName, tableLocation, isTrackedTable, customerNote }),
      }
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to create cart hold', code: json.code || 'CART_HOLD_ERROR' };
    }
    return { success: true, data: json.data, message: json.message };
  } catch (error) {
    console.error('createCartHold error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Update an existing cart-hold for showCode mode.
 * Retains the same pickup code while updating the cart items.
 *
 * @route PUT /api/v1/self-order/restaurant/:restaurantId/cart-hold/:cartHoldId
 * @access Firebase Auth
 */
export const updateCartHold = async (restaurantId, cartHoldId, { items, tableNumber = null, tableId = null, tableName = null, tableLocation = null, isTrackedTable = true, customerNote = '' }) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/cart-hold/${cartHoldId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ items, tableNumber, tableId, tableName, tableLocation, isTrackedTable, customerNote }),
      }
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to update cart hold', code: json.code || 'CART_HOLD_ERROR' };
    }
    return { success: true, data: json.data, message: json.message };
  } catch (error) {
    console.error('updateCartHold error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Check the status of a specific cart hold.
 * Allows the client to know if the cashier claimed the cart.
 *
 * @route GET /api/v1/self-order/restaurant/:restaurantId/cart-hold/:cartHoldId/status
 * @access Firebase Auth
 */
export const checkCartHoldStatus = async (restaurantId, cartHoldId) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/cart-hold/${cartHoldId}/status`
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to check cart hold status', code: json.code || 'CART_HOLD_ERROR' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('checkCartHoldStatus error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Create a Razorpay payment order (step 1 of payAndOrder flow).
 * Server recalculates totals server-side, saves a pending Order in MongoDB,
 * creates a Razorpay order, and returns the gateway credentials for the client checkout.
 *
 * @route POST /api/v1/self-order/restaurant/:restaurantId/payment/create-order
 * @access Firebase Auth
 */
export const createPaymentOrder = async (restaurantId, payload) => {
  try {
    const normalizedPayload = {
      ...payload,
      ...(payload.orderType ? { orderType: payload.orderType === 'pickup' ? 'takeaway' : payload.orderType } : {}),
      ...(payload.order_type ? { order_type: payload.order_type === 'pickup' ? 'takeaway' : payload.order_type } : {}),
    };

    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/payment/create-order`,
      { method: 'POST', body: JSON.stringify(normalizedPayload) }
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to create payment order', code: json.code || 'PAYMENT_ERROR' };
    }
    // Returns { gatewayOrderId, keyId, amount, currency, orderId, billing }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('createPaymentOrder error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Poll payment status for an order (fallback if Razorpay webhook is delayed).
 *
 * @route GET /api/v1/self-order/restaurant/:restaurantId/orders/:orderId/payment-status
 * @access Firebase Auth
 */
export const getPaymentStatus = async (restaurantId, orderId) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/orders/${orderId}/payment-status`
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch payment status', code: json.code || 'FETCH_ERROR' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getPaymentStatus error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Verify the Razorpay payment signature from the SDK handler response.
 * Sends razorpay_payment_id, razorpay_order_id, razorpay_signature to the server
 * for cryptographic HMAC-SHA256 verification.
 *
 * @route POST /api/v1/self-order/restaurant/:restaurantId/payment/verify
 * @access Firebase Auth
 */
export const verifyPayment = async (restaurantId, { razorpay_payment_id, razorpay_order_id, razorpay_signature, orderId }) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/payment/verify`,
      {
        method: 'POST',
        body: JSON.stringify({ razorpay_payment_id, razorpay_order_id, razorpay_signature, orderId }),
      }
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Signature verification failed', code: json.code || 'VERIFY_ERROR' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('verifyPayment error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

/**
 * Join the guest queue for a restaurant.
 * Can optionally include cart items for a "Pre-Order" join.
 */
export const updateQueueStatus = async (entryId, status, restaurantId) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/queue/${entryId}/cancel`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error || 'Failed to update queue status');
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('updateQueueStatus error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch the current user's active queue entry for a restaurant.
 */
export const getActiveQueueEntry = async (restaurantId, phone) => {
  try {
    const query = phone ? `?phone=${encodeURIComponent(phone)}` : '';
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/queue/active${query}`
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch queue entry' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getActiveQueueEntry error:', error);
    return { success: false, error: error.message };
  }
};

export const joinQueue = async (restaurantId, payload) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/queue`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to join queue', code: json.code || 'QUEUE_ERROR' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('joinQueue error:', error);
    return { success: false, error: error.message, code: 'NETWORK_ERROR' };
  }
};

export const checkQueueAvailability = async (restaurantId) => {
  try {
    const res = await fetch(`${BASE_URL}/self-order/restaurant/${restaurantId}/queue/availability`);
    const json = await res.json();
    return { success: true, ...json };
  } catch (error) {
    console.error('checkQueueAvailability error:', error);
    return { success: false, error: error.message };
  }
};

export const getQueueHistory = async (restaurantId, page = 1, limit = 20) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/queue/history?page=${page}&limit=${limit}`,
      { method: 'GET' }
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch history' };
    }
    return { success: true, data: json.data, pagination: json.pagination };
  } catch (error) {
    console.error('getQueueHistory error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch authoritative billing, payments, and credit note summary for an order.
 * Strictly scoped to order owner.
 */
export const getOrderBillingSummary = async (restaurantId, orderId) => {
  try {
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/orders/${orderId}/billing-summary`
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to fetch billing summary' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getOrderBillingSummary error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Lookup saved customer addresses by phone number for delivery checkout.
 */
export const lookupCustomerAddresses = async (restaurantId, phone) => {
  try {
    const cleanPhone = String(phone).replace(/\D/g, '').slice(-10);
    const res = await authFetch(
      `${BASE_URL}/self-order/restaurant/${restaurantId}/customer-lookup?phone=${encodeURIComponent(cleanPhone)}`
    );
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to lookup customer', code: json.code };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('lookupCustomerAddresses error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch available reservation slots for a given date and party size
 */
export const getReservationAvailability = async (restaurantId, { date, partySize, time } = {}) => {
  try {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (partySize) params.append('partySize', partySize);
    if (time) params.append('time', time);

    const res = await fetch(`${BASE_URL}/restaurants/${restaurantId}/reservations/availability?${params.toString()}`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to check availability' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getReservationAvailability error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a customer reservation (with optional pre-order)
 */
export const createCustomerReservation = async (restaurantId, payload) => {
  try {
    const res = await authFetch(`${BASE_URL}/restaurants/${restaurantId}/reservations`, {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        source: 'qr_menu',
      }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || json.message || 'Failed to create reservation' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('createCustomerReservation error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer reservation details
 */
export const getCustomerReservation = async (restaurantId, reservationId) => {
  try {
    const res = await authFetch(`${BASE_URL}/restaurants/${restaurantId}/reservations/${reservationId}`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Reservation not found' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getCustomerReservation error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cancel customer reservation
 */
export const cancelCustomerReservation = async (restaurantId, reservationId, reason) => {
  try {
    const res = await authFetch(`${BASE_URL}/restaurants/${restaurantId}/reservations/${reservationId}/status`, {
      method: 'PUT',
      body: JSON.stringify({
        status: 'cancelled',
        cancelledBy: 'customer',
        cancellationReason: reason || 'Customer cancelled via web',
      }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Failed to cancel reservation' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('cancelCustomerReservation error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch customer loyalty dashboard using offline-printed QR token
 */
export const getCustomerPortalDashboard = async (token) => {
  try {
    const res = await fetch(`${BASE_URL}/self-order/portal/dashboard?token=${encodeURIComponent(token)}`);
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { success: false, error: json.error || 'Invalid or expired loyalty session' };
    }
    return { success: true, data: json.data };
  } catch (error) {
    console.error('getCustomerPortalDashboard error:', error);
    return { success: false, error: error.message };
  }
};




